import React, { useEffect, useState } from "react";
import { Mail, Pencil, Check } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const DEFAULT_TEMPLATES = [
  { event: "ticket_opened",    label: "Ticket Opened",      subject: "Your support ticket {{ticket_id}} has been received", body: "Hi {{requester_name}},\n\nWe've received your ticket: {{subject}}\n\nTicket ID: {{ticket_id}}\nExpected response: {{sla_response_time}}\n\nWe'll keep you updated.\n\nSupport Team" },
  { event: "ticket_assigned",  label: "Ticket Assigned",    subject: "Ticket {{ticket_id}} has been assigned",              body: "Hi {{requester_name}},\n\nYour ticket {{ticket_id}} has been assigned to {{assignee}}.\n\nWe're working on it and will update you shortly.\n\nSupport Team" },
  { event: "ticket_updated",   label: "Ticket Updated",     subject: "Update on your ticket {{ticket_id}}",                body: "Hi {{requester_name}},\n\nThere's an update on your ticket {{ticket_id}}:\n\n{{update_message}}\n\nSupport Team" },
  { event: "ticket_escalated", label: "Ticket Escalated",   subject: "Ticket {{ticket_id}} has been escalated",            body: "Hi {{requester_name}},\n\nYour ticket {{ticket_id}} has been escalated to our senior team for faster resolution.\n\nSupport Team" },
  { event: "ticket_on_hold",   label: "Ticket On Hold",     subject: "Ticket {{ticket_id}} is on hold",                   body: "Hi {{requester_name}},\n\nYour ticket {{ticket_id}} has been placed on hold.\n\nReason: {{hold_reason}}\n\nWe'll resume as soon as possible.\n\nSupport Team" },
  { event: "ticket_closed",    label: "Ticket Closed",      subject: "Ticket {{ticket_id}} has been resolved",             body: "Hi {{requester_name}},\n\nYour ticket {{ticket_id}} has been closed.\n\nResolution: {{resolution_note}}\n\nIf you need further help, please reply to this email.\n\nSupport Team" },
  { event: "sla_breach",       label: "SLA Breach Alert",   subject: "⚠ SLA breach: ticket {{ticket_id}}",                body: "Hi {{agent_name}},\n\nSLA has been breached for ticket {{ticket_id}} ({{priority}}).\n\nRequester: {{requester_name}}\nBreached at: {{breach_time}}\n\nPlease take immediate action.\n\nSupport System" },
];

const VARS = ["{{ticket_id}}","{{requester_name}}","{{subject}}","{{assignee}}","{{priority}}","{{sla_response_time}}","{{update_message}}","{{hold_reason}}","{{resolution_note}}","{{agent_name}}","{{breach_time}}"];

export default function NotificationTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [editing,   setEditing]   = useState(null);
  const [draft,     setDraft]     = useState({});
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState(null);
  const token = localStorage.getItem("token");
  const authH = () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" });

  useEffect(() => {
    fetch(`${API}/api/admin/tickets/notification-templates`, { headers: authH() })
      .then(r => r.json())
      .then(d => setTemplates(Array.isArray(d) && d.length > 0 ? d : DEFAULT_TEMPLATES))
      .catch(() => setTemplates(DEFAULT_TEMPLATES))
      .finally(() => setLoading(false));
  }, []);

  const startEdit = (t) => { setDraft({ ...t }); setEditing(t.event); setMsg(null); };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      // Backend expects PUT /notification-templates with the event in the body
      // (NotificationTemplateIn includes event). `draft` already carries it.
      const res = await fetch(`${API}/api/admin/tickets/notification-templates`, {
        method: "PUT", headers: authH(), body: JSON.stringify(draft),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setTemplates(ts => ts.map(t => t.event === draft.event ? draft : t));
      setEditing(null);
      setMsg({ type: "success", text: "✓ Template saved." });
    } catch (e) { setMsg({ type: "error", text: `⚠ ${e.message}` }); }
    finally { setSaving(false); }
  };

  if (loading) return <div className={`${card} p-5`}><div className="h-24 rounded-lg bg-white/5 animate-pulse" /></div>;

  return (
    <div className={card}>
      <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
        <Mail size={13} className="text-[#00ff90]/60" />
        <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Notification Templates</span>
      </div>

      {msg && (
        <div className={`mx-5 mt-3 rounded-lg px-4 py-2 text-xs border ${msg.type === "error" ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90]"}`}>
          {msg.text}
        </div>
      )}

      <div className="divide-y divide-white/5">
        {templates.map(t => (
          <div key={t.event}>
            <div className="flex items-center justify-between px-5 py-3">
              <div>
                <p className="text-sm text-white/70 font-medium">{t.label}</p>
                <p className="text-xs text-white/30 truncate max-w-xs">{t.subject}</p>
              </div>
              <button onClick={() => editing === t.event ? setEditing(null) : startEdit(t)}
                className="w-7 h-7 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/30 hover:text-[#00ff90] transition-colors">
                {editing === t.event ? <Check size={12} /> : <Pencil size={12} />}
              </button>
            </div>

            {editing === t.event && (
              <div className="px-5 pb-4 bg-[#00ff90]/3 space-y-3">
                <div>
                  <label className={labelCls}>Subject</label>
                  <input className={inputCls} value={draft.subject || ""} onChange={e => setDraft(d => ({...d, subject: e.target.value}))} />
                </div>
                <div>
                  <label className={labelCls}>Body</label>
                  <textarea rows={6} className="w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 resize-none font-mono"
                    value={draft.body || ""} onChange={e => setDraft(d => ({...d, body: e.target.value}))} />
                </div>
                <div>
                  <p className="text-xs text-white/25 mb-1.5">Available variables:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {VARS.map(v => (
                      <button key={v} onClick={() => setDraft(d => ({...d, body: (d.body || "") + v}))}
                        className="text-xs px-2 py-0.5 rounded-full border border-[#00ff90]/15 bg-[#00ff90]/5 text-[#00ff90]/50 hover:bg-[#00ff90]/10 hover:text-[#00ff90]/70 transition-colors">
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditing(null)} className="text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
                  <button onClick={save} disabled={saving}
                    className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">
                    {saving ? "Saving…" : "Save Template"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}